import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import { Eye, Search, RefreshCw, Calendar, User, ArrowUp, ArrowDown, ArrowUpDown, ExternalLink, RotateCcw, ChevronDown, Check, History } from "lucide-react";
import AssignmentThreadView from "./assignments/AssignmentThreadView";
import ClassicAssignmentDetails from "./assignments/ClassicAssignmentDetails";
import RequestHistoryModal from "../components/RequestHistoryModal";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";
import { useReviewMode } from "../context/ReviewModeContext";
import { useAccess } from "../context/AccessContext";
import { isStrictTeamMember } from "../utils/profileDisplayRole";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";
const FILESYSTEM = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";

function previewKindFromPath(path) {
  const lower = String(path || "").toLowerCase().split("?")[0];
  if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(lower)) return "image";
  if (/\.(tif|tiff|pdf)$/.test(lower)) return "pdf";
  if (lower.endsWith(".eml")) return "html";
  return "file";
}

function needsConvertedPreview(path) {
  const lower = String(path || "").toLowerCase().split("?")[0];
  return (
    lower.endsWith(".tif") ||
    lower.endsWith(".tiff") ||
    lower.endsWith(".eml") ||
    lower.endsWith(".heic") ||
    lower.endsWith(".heif")
  );
}

const PREVIEW_CACHE_LIMIT = 8;
function rememberPreview(cache, path, url, kind) {
  if (cache.has(path)) cache.delete(path);
  cache.set(path, { url, kind });
  while (cache.size > PREVIEW_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    const oldest = cache.get(oldestKey);
    cache.delete(oldestKey);
    if (oldest?.url?.startsWith("blob:") && oldest.url !== url) {
      URL.revokeObjectURL(oldest.url);
    }
  }
}

/* ─────────────────────────────────────────────────────────────
   SEARCHABLE SELECT COMPONENT
───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [], // [{ value, label, sub }]
  value,
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 200, openUpward: false });
  const [inputValue, setInputValue] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) {
      setInputValue(selected ? (selected.label || selected.value) : (value && value !== "all" ? value : ""));
    }
  }, [value, selected, open]);

  const updatePosition = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 240 && rect.top > spaceBelow;
      const width = Math.min(Math.max(220, rect.width), window.innerWidth - 20);

      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = window.innerWidth - width - 10;
      }
      if (left < 10) left = 10;

      let top = 0;
      if (openUpward) {
        top = rect.top - 4;
      } else {
        top = rect.bottom + 4;
      }

      setCoords({ top, left, width, openUpward });
    }
  };

  const checkPositionAndOpen = () => {
    if (disabled || loading) return;
    updatePosition();
    setOpen(true);
    setFocusedIndex(0);
  };

  useEffect(() => {
    if (!open) return;
    const handleScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrap && !inDropdown) {
        setOpen(false);
        setInputValue(selected ? (selected.label || selected.value) : "");
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleMouseDown);
    }
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, selected]);

  const filtered = useMemo(() => {
    let list = options;
    const q = (inputValue || "").trim().toLowerCase();
    const selLabel = (selected ? (selected.label || selected.value) : "").trim().toLowerCase();
    if (open && q && q !== selLabel) {
      list = options.filter((o) => {
        const lbl = (o.label || "").toLowerCase();
        const sub = (o.sub || "").toLowerCase();
        return lbl.includes(q) || sub.includes(q);
      });
    }
    return list;
  }, [options, inputValue, open, selected]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    const item = options.find((o) => o.value === val);
    setInputValue(item ? (item.label || item.value) : "");
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        checkPositionAndOpen();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % Math.max(1, filtered.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + filtered.length) % Math.max(1, filtered.length));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        } else if (filtered.length > 0) {
          handleSelect(filtered[0].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setInputValue(selected ? (selected.label || selected.value) : "");
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", minWidth: 0 }} id={id} onClick={(e) => e.stopPropagation()}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "#f8fafc",
          border: open ? `1px solid ${THEME}` : "1px solid #cbd5e1",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
          borderRadius: "8px",
          padding: "0 10px",
          height: "36px",
          transition: "all 0.15s ease",
          cursor: disabled || loading ? "not-allowed" : "text",
          opacity: disabled ? 0.6 : 1,
          boxSizing: "border-box",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          disabled={disabled || loading}
          placeholder={loading ? "Loading…" : (selected ? selected.label : placeholder)}
          onClick={() => {
            if (!open) checkPositionAndOpen();
          }}
          onFocus={() => {
            checkPositionAndOpen();
          }}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!open) checkPositionAndOpen();
            setFocusedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          style={{
            border: "none",
            outline: "none",
            background: "transparent",
            width: "100%",
            minWidth: 0,
            fontSize: "12.5px",
            fontWeight: selected && selected.value !== "all" ? "600" : "500",
            color: selected && selected.value !== "all" ? "#0f172a" : "#475569",
            padding: 0,
            cursor: disabled || loading ? "not-allowed" : "text",
            textOverflow: "ellipsis",
          }}
        />
        <ChevronDown
          size={14}
          color="#64748b"
          style={{
            flexShrink: 0,
            marginLeft: "6px",
            cursor: "pointer",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (open) {
              setOpen(false);
              setInputValue(selected ? (selected.label || selected.value) : "");
            } else {
              checkPositionAndOpen();
              inputRef.current?.focus();
            }
          }}
        />
      </div>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: coords.openUpward ? "translateY(-100%)" : "none",
              zIndex: 999999,
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              boxShadow: coords.openUpward
                ? "0 -10px 25px -5px rgba(0, 0, 0, 0.18), 0 -8px 10px -6px rgba(0, 0, 0, 0.1)"
                : "0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              width: `${coords.width}px`,
              maxHeight: "240px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              padding: "4px 0",
              animation: "fadeIn 0.12s ease-out",
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
                No options found
              </div>
            ) : (
              filtered.map((o, idx) => {
                const isActive = o.value === value;
                const isFocused = idx === focusedIndex;

                return (
                  <div
                    key={o.value}
                    onClick={() => handleSelect(o.value)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: isFocused ? "#f1f5f9" : isActive ? THEME_LIGHT : "transparent",
                      cursor: "pointer",
                      borderLeft: isActive ? `3px solid ${THEME}` : "3px solid transparent",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: "12.5px",
                          fontWeight: isActive ? "700" : "500",
                          color: isActive ? THEME : "#1e293b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {o.label || o.value}
                      </span>
                      {o.sub && (
                        <span
                          style={{
                            fontSize: "10.5px",
                            color: "#64748b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {o.sub}
                        </span>
                      )}
                    </div>
                    {isActive && <Check size={13} color={THEME} style={{ flexShrink: 0, marginLeft: "6px" }} />}
                  </div>
                );
              })
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

function InlineAssigneeCell({ item, assignableUsers, inlineUpdatingId, onAssignChange }) {
  const { instance, accounts } = useMsal();
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 240, openUpward: false });
  const [inputValue, setInputValue] = useState(item.assignedTo || "");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [deptUsers, setDeptUsers] = useState(null);
  const [loadingDeptUsers, setLoadingDeptUsers] = useState(false);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const fetchDeptUsers = useCallback(async () => {
    const targetDept = item.currentDepartment || item.department;
    const targetDeptId = item.currentDepartmentId || item.departmentId;
    if (!targetDept && !targetDeptId) {
      setLoadingDeptUsers(false);
      return;
    }

    try {
      setLoadingDeptUsers(true);
      const org = item.organization || "";
      const proj = item.project || "";
      let url = `/api/assignments/assignable-users?organization=${encodeURIComponent(org)}&project=${encodeURIComponent(proj)}&_t=${Date.now()}`;
      if (targetDeptId) {
        url += `&departmentId=${targetDeptId}`;
      } else if (targetDept) {
        url += `&department=${encodeURIComponent(targetDept)}`;
      }
      const res = await callApi(instance, accounts, "GET", url);
      const data = res?.data?.data || res?.data || [];
      if (Array.isArray(data) && data.length > 0) {
        setDeptUsers(data);
      }
    } catch (err) {
      console.error("Failed to fetch department specific users in inline cell:", err);
    } finally {
      setLoadingDeptUsers(false);
    }
  }, [instance, accounts, item.currentDepartment, item.department, item.currentDepartmentId, item.departmentId, item.organization, item.project]);

  const updatePosition = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 240 && rect.top > spaceBelow;
      const width = Math.min(Math.max(240, rect.width), window.innerWidth - 20);

      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = window.innerWidth - width - 10;
      }
      if (left < 10) left = 10;

      let top = 0;
      if (openUpward) {
        top = rect.top - 4;
      } else {
        top = rect.bottom + 4;
      }

      setCoords({ top, left, width, openUpward });
    }
  };

  const checkPositionAndOpen = () => {
    updatePosition();
    setIsOpen(true);
    setHighlightedIndex(0);
    fetchDeptUsers();
  };

  useEffect(() => {
    if (!isOpen) {
      setInputValue(item.assignedTo || "");
    }
  }, [item.assignedTo, isOpen]);

  // Keep dropdown anchored if user scrolls or resizes
  useEffect(() => {
    if (!isOpen) return;
    const handleScrollOrResize = () => {
      updatePosition();
    };
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  // Handle click outside (checking both input wrap and portalled dropdown)
  useEffect(() => {
    const handleMouseDown = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrap && !inDropdown) {
        setIsOpen(false);
        setInputValue(item.assignedTo || "");
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleMouseDown);
    }
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, item.assignedTo]);

  const filteredUsers = useMemo(() => {
    let list = (deptUsers && deptUsers.length > 0) ? deptUsers : assignableUsers;
    const q = (inputValue || "").trim().toLowerCase();
    if (isOpen && q && q !== (item.assignedTo || "").trim().toLowerCase()) {
      list = list.filter(u => {
        const name = (u.displayName || "").toLowerCase();
        const email = (u.mailAddress || u.uniqueName || u.principalName || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    // Pin current assignee to the very top
    return [...list].sort((a, b) => {
      const isCurrentA = (item.assignedToEmail && item.assignedToEmail.toLowerCase() === (a.mailAddress || a.uniqueName || a.principalName || "").toLowerCase()) ||
        (item.assignedTo && item.assignedTo.toLowerCase() === (a.displayName || "").toLowerCase());
      const isCurrentB = (item.assignedToEmail && item.assignedToEmail.toLowerCase() === (b.mailAddress || b.uniqueName || b.principalName || "").toLowerCase()) ||
        (item.assignedTo && item.assignedTo.toLowerCase() === (b.displayName || "").toLowerCase());
      if (isCurrentA && !isCurrentB) return -1;
      if (!isCurrentA && isCurrentB) return 1;
      return 0;
    });
  }, [deptUsers, assignableUsers, inputValue, isOpen, item.assignedTo, item.assignedToEmail]);

  const handleSelect = (user) => {
    setIsOpen(false);
    setInputValue(user ? user.displayName : "Unassigned");
    onAssignChange(item, user);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        checkPositionAndOpen();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % Math.max(1, filteredUsers.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + filteredUsers.length) % Math.max(1, filteredUsers.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredUsers.length > 0 && highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
        handleSelect(filteredUsers[highlightedIndex]);
      } else if (filteredUsers.length > 0) {
        handleSelect(filteredUsers[0]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setInputValue(item.assignedTo || "");
      inputRef.current?.blur();
    }
  };

  const isUpdating = inlineUpdatingId === item.id;

  return (
    <div
      ref={wrapRef}
      className="inline-assignee-dropdown-wrap"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        maxWidth: "100%",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          width: "fit-content",
          maxWidth: "100%",
          background: isOpen ? "#ffffff" : "transparent",
          border: isOpen ? "1px solid #3b82f6" : "1px solid transparent",
          boxShadow: isOpen ? "0 0 0 2px rgba(59, 130, 246, 0.15)" : "none",
          borderRadius: "6px",
          padding: "3px 6px",
          transition: "all 0.15s ease",
          cursor: "pointer",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
        title={!isOpen ? (item.assignedTo || "Unassigned") : undefined}
        onClick={() => {
          if (!isOpen && !isUpdating) {
            checkPositionAndOpen();
          }
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = "#f8fafc";
            e.currentTarget.style.borderColor = "#e2e8f0";
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "transparent";
          }
        }}
      >
        {isUpdating ? (
          <RefreshCw size={13} color="#2563eb" style={{ animation: "spin 0.8s linear infinite", flexShrink: 0, marginRight: "5px" }} />
        ) : (
          <User size={13} color={item.assignedTo ? "#2563eb" : "#94a3b8"} style={{ flexShrink: 0, marginRight: "5px" }} />
        )}
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            disabled={isUpdating}
            placeholder="Unassigned"
            autoFocus
            onChange={(e) => {
              setInputValue(e.target.value);
              setHighlightedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              width: "110px",
              minWidth: 0,
              fontSize: "12px",
              fontWeight: item.assignedTo ? "600" : "400",
              color: item.assignedTo ? "#1e293b" : "#94a3b8",
              padding: 0,
              cursor: "text",
            }}
          />
        ) : (
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "12px",
              fontWeight: item.assignedTo ? "600" : "400",
              color: item.assignedTo ? "#1e293b" : "#94a3b8",
              display: "inline-block",
              maxWidth: "120px",
            }}
          >
            {item.assignedTo || "Unassigned"}
          </span>
        )}
        <ChevronDown
          size={12}
          color="#94a3b8"
          style={{
            flexShrink: 0,
            marginLeft: "4px",
            cursor: "pointer",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease"
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isOpen) {
              setIsOpen(false);
              setInputValue(item.assignedTo || "");
            } else {
              checkPositionAndOpen();
            }
          }}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: coords.openUpward ? "translateY(-100%)" : "none",
              zIndex: 999999,
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              boxShadow: coords.openUpward
                ? "0 -10px 25px -5px rgba(0, 0, 0, 0.18), 0 -8px 10px -6px rgba(0, 0, 0, 0.1)"
                : "0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              width: `${coords.width}px`,
              maxHeight: "220px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              padding: "4px 0",
              animation: "fadeIn 0.12s ease-out"
            }}
          >
            {loadingDeptUsers ? (
              <div style={{ padding: "14px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "12px", color: "#64748b" }}>
                <RefreshCw size={14} color="#2563eb" style={{ animation: "spin 0.8s linear infinite" }} />
                <span>Loading department members...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
                No members found
              </div>
            ) : (
              filteredUsers.map((u, idx) => {
                const uName = u.displayName || u.mailAddress || u.uniqueName || "User";
                const uEmail = u.mailAddress || u.uniqueName || u.principalName || "";
                const isCurrent = (item.assignedToEmail && item.assignedToEmail.toLowerCase() === uEmail.toLowerCase()) ||
                  (item.assignedTo && item.assignedTo.toLowerCase() === uName.toLowerCase());
                const isHighlighted = idx === highlightedIndex;

                return (
                  <div
                    key={idx}
                    onClick={() => handleSelect(u)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "7px 12px",
                      background: isHighlighted ? "#f1f5f9" : isCurrent ? "#eff6ff" : "transparent",
                      cursor: "pointer",
                      borderLeft: isCurrent ? "3px solid #2563eb" : "3px solid transparent"
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span style={{ fontSize: "12px", fontWeight: isCurrent ? "700" : "500", color: isCurrent ? "#2563eb" : "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {uName}
                      </span>
                      {uEmail && (
                        <span style={{ fontSize: "10px", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {uEmail}
                        </span>
                      )}
                    </div>
                    {isCurrent && <Check size={13} color="#2563eb" style={{ flexShrink: 0, marginLeft: "6px" }} />}
                  </div>
                );
              })
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function ViewAssignment({ onBack }) {
  const { instance, accounts } = useMsal();
  const { isAdmin, hierarchyRoleCode, roles, isTeamMember } = useAccess();

  const isOnlyTeamMember = useMemo(() => {
    if (typeof isTeamMember === "boolean") return isTeamMember;
    return isStrictTeamMember({ isAdmin, hierarchyRoleCode, roles });
  }, [isTeamMember, isAdmin, hierarchyRoleCode, roles]);

  /* ── FILTER STATES ── */
  const [searchId, setSearchId] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("DWS10062665");
  const [selectedProject, setSelectedProject] = useState("ADLS Migration");
  const [selectedIteration, setSelectedIteration] = useState("all");
  const [selectedAssignee, setSelectedAssignee] = useState("all");
  const [sortBy, setSortBy] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("desc");

  /* ── DATA STATES ── */
  const [orgProjects, setOrgProjects] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);

  /* ── PAGINATION STATES ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [totalItems, setTotalItems] = useState(0);
  const [gotoPageInput, setGotoPageInput] = useState("");

  /* ── UI STATES ── */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /* ── VIEW DETAIL STATE ── */
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);
  const [historyModalItem, setHistoryModalItem] = useState(null);

  /* ── TABLE INLINE ASSIGNEE STATES ── */
  const [inlineUpdatingId, setInlineUpdatingId] = useState(null);

  const handleInlineAssigneeChange = async (item, newUser) => {
    try {
      setInlineUpdatingId(item.id);
      const org = item.organization || selectedOrg || "all";
      const proj = item.project || selectedProject;
      const newAssigneeVal = newUser ? (newUser.uniqueName || newUser.mailAddress || newUser.principalName || newUser.displayName) : "";
      const newAssigneeName = newUser ? newUser.displayName : "Unassigned";
      const newAssigneeEmail = newUser ? (newUser.uniqueName || newUser.mailAddress || "") : "";

      const res = await callApi(
        instance, accounts, "PATCH",
        `/api/assignments/task/${item.id}?organization=${org}&project=${proj}`,
        { assignedTo: newAssigneeVal }
      );

      if (res?.data?.success || res?.status === 200) {
        // Optimistically update local assignments list
        setAssignments(prev => prev.map(a => a.id === item.id ? {
          ...a,
          assignedTo: newAssigneeName,
          assignedToEmail: newAssigneeEmail
        } : a));
      }
    } catch (err) {
      console.error("Failed to update assignee inline:", err);
    } finally {
      setInlineUpdatingId(null);
    }
  };

  // Location and navigation hooks
  const location = useLocation();
  const navigate = useNavigate();
  const directLinkId = new URLSearchParams(location.search).get("id");
  const isDirectLink = Boolean(directLinkId);
  const fromParam = new URLSearchParams(location.search).get("from");

  const goToRequestPoolList = () => {
    setSelectedDetailItem(null);
    setError("");
    setLoading(false);
    const searchParams = new URLSearchParams(location.search || window.location.search);
    const currentFrom = searchParams.get("from") || fromParam || location.state?.from;
    if (currentFrom === "manual-assignment" || currentFrom === "/manual-assignment") {
      navigate("/manual-assignment", { replace: true });
      return;
    }
    if (isDirectLink || searchParams.has("id") || searchParams.has("q")) {
      navigate("/request-pool", { replace: true });
      return;
    }
    setSelectedDetailItem(null);
  };

  const { setReviewMode } = useReviewMode();

  useEffect(() => {
    if (selectedDetailItem) {
      const isManual =
        selectedDetailItem.is_manual === true ||
        !selectedDetailItem.thread ||
        !selectedDetailItem.thread.emails ||
        selectedDetailItem.thread.emails.length === 0 ||
        !selectedDetailItem.thread.emails.some((e) => e && (e.email_storage_path || (e.msg_id && e.msg_id > 0 && e.msg_id !== selectedDetailItem.id)));

      if (!isManual) {
        setReviewMode(true);
      } else {
        setReviewMode(false);
      }
    } else {
      setReviewMode(false);
    }
    return () => {
      setReviewMode(false);
    };
  }, [selectedDetailItem, setReviewMode]);

  const previewCacheRef = useRef(new Map());
  const previewAbortRef = useRef(null);
  const previewPathRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [selectedReviewRow, setSelectedReviewRow] = useState(null);
  const [selectedMsgId, setSelectedMsgId] = useState(null);
  const [inlinePreviewUrl, setInlinePreviewUrl] = useState(null);
  const [inlinePreviewKind, setInlinePreviewKind] = useState("file");
  const [inlinePreviewError, setInlinePreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const getPreviewTargetUrl = useCallback(
    async (path, signal) => {
      const cleaned = path?.trim();
      if (!cleaned) throw new Error("Preview path missing");

      const { filesystem, path: filePath } = resolveAdlsPath(cleaned, FILESYSTEM);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      if (needsConvertedPreview(filePath)) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });
        const response = await fetch(
          `${baseUrl}/api/adls/preview-blob` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) throw new Error(`Failed to preview file (${response.status})`);
        const blob = await response.blob();
        if (!blob || blob.size === 0) throw new Error("Empty preview response");
        return URL.createObjectURL(blob);
      }

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal,
          params: { filesystem, path: filePath },
        },
      );

      if (!res?.data?.url) throw new Error("Preview URL missing");
      return res.data.url;
    },
    [accounts, instance]
  );

  const handlePreview = useCallback(
    async (path) => {
      const cleaned = path?.trim();
      if (!cleaned) return;

      const kind = previewKindFromPath(cleaned);
      const cached = previewCacheRef.current.get(cleaned);
      if (cached?.url) {
        previewAbortRef.current?.abort();
        previewPathRef.current = cleaned;
        previewUrlRef.current = cached.url;
        setInlinePreviewError(null);
        setInlinePreviewKind(cached.kind || kind);
        setInlinePreviewUrl(cached.url);
        setPreviewLoading(false);
        return;
      }

      if (
        previewPathRef.current === cleaned &&
        previewAbortRef.current &&
        !previewAbortRef.current.signal.aborted
      ) {
        return;
      }

      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      previewPathRef.current = cleaned;

      try {
        setPreviewLoading(true);
        setInlinePreviewError(null);
        setInlinePreviewKind(kind);
        setInlinePreviewUrl(null);

        const url = await getPreviewTargetUrl(cleaned, controller.signal);
        if (controller.signal.aborted) {
          if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
          return;
        }

        rememberPreview(previewCacheRef.current, cleaned, url, kind);
        previewUrlRef.current = url;
        setInlinePreviewUrl(url);
      } catch (err) {
        if (err?.name === "AbortError" || err?.message === "Request cancelled") return;
        if (previewPathRef.current === cleaned) previewPathRef.current = null;
        console.error("Preview failed", err);
        setInlinePreviewError(err?.message || "Failed to preview document");
      } finally {
        if (previewAbortRef.current === controller) {
          previewAbortRef.current = null;
        }
        if (!controller.signal.aborted) {
          setPreviewLoading(false);
        }
      }
    },
    [getPreviewTargetUrl]
  );

  // Watch URL query parameters to auto-load assignment details if linked from notifications
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const id = query.get("id");
    const q = query.get("q");
    const org = query.get("org");
    const proj = query.get("proj");

    if (id) {
      const loadDirectDetail = async () => {
        try {
          setLoading(true);
          setError("");
          const detailsRes = await callApi(
            instance, accounts, "GET",
            `/api/assignments/task/${id}?organization=${org || "all"}&_t=${Date.now()}`
          );
          const item = detailsRes?.data?.data || detailsRes?.data || null;
          if (item && (item.id || item.title)) {
            item.organization = org || item.organization || selectedOrg;
            item.project = proj || item.project || selectedProject;
            setSelectedDetailItem(item);

            const activeRow = item.selectedRow || item.thread?.emails?.[0] || null;
            setSelectedReviewRow(activeRow);
            setSelectedMsgId(activeRow?.msg_id || null);
            setInlinePreviewUrl(null);
            setInlinePreviewError(null);

            if (activeRow?.email_storage_path) {
              handlePreview(activeRow.email_storage_path);
            } else if (activeRow?.attachments?.[0]?.path) {
              handlePreview(activeRow.attachments[0].path);
            }
          } else {
            setError(detailsRes?.data?.message || "Assignment details not found or could not be loaded.");
          }
        } catch (err) {
          console.error("Failed to load direct assignment detail", err);
          setError(err?.response?.data?.message || "Failed to load assignment details. It may have been deleted.");
        } finally {
          setLoading(false);
        }
      };
      loadDirectDetail();
    } else {
      if (q) {
        setSearchId(q);
      }
      setSelectedDetailItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, instance, accounts, selectedOrg, selectedProject]);

  /* =====================================================
     LOAD INITIAL ORG/PROJECT LIST
  ===================================================== */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await callApi(
          instance, accounts, "GET",
          "/api/assignments/organizations/projects"
        );
        const data = res?.data?.data || res?.data || [];
        const orgList = Array.isArray(data) ? data : [];
        setOrgProjects(orgList);
        if (orgList.length > 0) {
          const firstOrg = orgList[0];
          setSelectedOrg(firstOrg.organization);
          const adlsProj = firstOrg.projects?.find(p => (p.name || p).toLowerCase() === "adls migration");
          setSelectedProject(adlsProj ? (adlsProj.name || adlsProj) : "ADLS Migration");
        }
      } catch (err) {
        console.error("Failed to load org/project config", err);
      }
    };
    load();
  }, [instance, accounts]);

  /* =====================================================
     LOAD SPRINT & ASSIGNEE OPTIONS DYNAMICALLY
  ===================================================== */
  useEffect(() => {
    if (selectedOrg === "all") {
      setIterations([]);
      setAssignableUsers([]);
      setSelectedIteration("all");
      setSelectedAssignee("all");
      return;
    }

    const loadOptions = async () => {
      try {
        const projectParam = selectedProject !== "all" ? `&project=${encodeURIComponent(selectedProject)}` : "";

        // Load Iterations
        const iterRes = await callApi(
          instance, accounts, "GET",
          `/api/assignments/iterations?organization=${encodeURIComponent(selectedOrg)}${projectParam}&_t=${Date.now()}`
        );
        const iterData = iterRes?.data?.data || iterRes?.data || [];
        setIterations(Array.isArray(iterData) ? iterData : []);

        // Load Users
        const userRes = await callApi(
          instance, accounts, "GET",
          `/api/assignments/assignable-users?organization=${encodeURIComponent(selectedOrg)}${projectParam}&_t=${Date.now()}`
        );
        const userData = userRes?.data?.data || userRes?.data || [];
        const userList = Array.isArray(userData) ? userData : [];
        setAssignableUsers(userList);

        if (isOnlyTeamMember && userList.length === 1) {
          const singleVal = userList[0].uniqueName || userList[0].mailAddress || userList[0].principalName || userList[0].displayName;
          setSelectedAssignee(singleVal);
        }
      } catch (err) {
        console.error("Failed to load sub-filter options", err);
      }
    };

    loadOptions();
  }, [selectedOrg, selectedProject, instance, accounts]);

  /* =====================================================
     LOAD SINGLE ASSIGNMENT DETAILS
  ===================================================== */
  const handleLoadItemDetails = async (id, org) => {
    try {
      setLoading(true);
      setError("");
      const detailsRes = await callApi(
        instance, accounts, "GET",
        `/api/assignments/task/${id}?organization=${org || "all"}&_t=${Date.now()}`
      );
      const item = detailsRes?.data?.data || detailsRes?.data || null;
      if (item) {
        item.organization = org || item.organization;
        setSelectedDetailItem(item);

        const activeRow = item.selectedRow || item.thread?.emails?.[0] || null;
        setSelectedReviewRow(activeRow);
        setSelectedMsgId(activeRow?.msg_id || null);
        setInlinePreviewUrl(null);
        setInlinePreviewError(null);

        if (activeRow?.email_storage_path) {
          handlePreview(activeRow.email_storage_path);
        } else if (activeRow?.attachments?.[0]?.path) {
          handlePreview(activeRow.attachments[0].path);
        }
      } else {
        setError("Failed to load work item details.");
      }
    } catch (err) {
      console.error("Failed to load work item details", err);
      setError(err?.response?.data?.message || "Failed to load work item details.");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     LOAD ASSIGNMENTS LIST WITH DYNAMIC FILTERS
  ===================================================== */
  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      let url = `/api/assignments/list?_t=${Date.now()}`;
      if (searchId.trim()) {
        url += `&id=${encodeURIComponent(searchId.trim())}`;
      } else {
        url += `&page=${currentPage}&limit=10&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      }

      // Always pass user's filters to respect selection criteria
      if (selectedOrg !== "all") url += `&organization=${encodeURIComponent(selectedOrg)}`;
      if (selectedProject !== "all") url += `&project=${encodeURIComponent(selectedProject)}`;
      if (selectedIteration !== "all") url += `&iterationPath=${encodeURIComponent(selectedIteration)}`;
      if (selectedAssignee !== "all") url += `&assignedTo=${encodeURIComponent(selectedAssignee)}`;

      const res = await callApi(instance, accounts, "GET", url);
      const resObj = res?.data?.data || res?.data || {};

      if (searchId.trim()) {
        const items = Array.isArray(resObj.items) ? resObj.items : (Array.isArray(resObj) ? resObj : []);
        setAssignments(items);
        setTotalItems(items.length);
        setTotalPages(items.length ? 1 : 0);
      } else {
        setAssignments(Array.isArray(resObj.items) ? resObj.items : []);
        setTotalItems(resObj.total || 0);
        setTotalPages(resObj.totalPages || 0);
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to load assignments list.");
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, selectedProject, selectedIteration, selectedAssignee, searchId, currentPage, sortBy, sortOrder, instance, accounts]);

  // Reset page to 1 when filters, search ID, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrg, selectedProject, selectedIteration, selectedAssignee, searchId, sortBy, sortOrder]);

  // Initial load / trigger on changes
  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadAssignments();
  };

  const handleRefresh = () => {
    loadAssignments();
  };

  const handleClearFilters = () => {
    setSearchId("");
    if (orgProjects.length > 0) {
      const firstOrg = orgProjects[0];
      setSelectedOrg(firstOrg.organization);
      if (firstOrg.projects && firstOrg.projects.length > 0) {
        setSelectedProject(firstOrg.projects[0].name || firstOrg.projects[0]);
      } else {
        setSelectedProject("all");
      }
    } else {
      setSelectedOrg("all");
      setSelectedProject("all");
    }
    setSelectedIteration("all");
    if (isOnlyTeamMember && assignableUsers.length === 1) {
      const singleVal = assignableUsers[0].uniqueName || assignableUsers[0].mailAddress || assignableUsers[0].principalName || assignableUsers[0].displayName;
      setSelectedAssignee(singleVal);
    } else {
      setSelectedAssignee("all");
    }
    setSortBy("id");
    setSortOrder("desc");
    setCurrentPage(1);
    setError("");
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder(field === "id" ? "desc" : "asc");
    }
    setCurrentPage(1);
  };

  const iterationOptions = [
    { value: "all", label: selectedProject === "all" ? "Select project first" : "All Sprints" },
    ...iterations.map((iter) => ({ value: iter.path || iter.name, label: iter.name }))
  ];

  const assigneeOptions = [
    ...(!isOnlyTeamMember || assignableUsers.length > 1
      ? [{ value: "all", label: selectedProject === "all" ? "Select project first" : "All Assignees" }]
      : []),
    ...(!isOnlyTeamMember
      ? [{ value: "Unassigned", label: "Unassigned", sub: "Unassigned requests" }]
      : []),
    ...assignableUsers.map((u) => ({
      value: u.uniqueName || u.mailAddress || u.principalName || u.displayName,
      label: u.displayName,
      sub: (u.mailAddress || u.uniqueName || u.principalName || "").toLowerCase()
    }))
  ];

  /* Shared column widths */
  const renderColGroup = () => (
    <colgroup>
      {!isOnlyTeamMember && <col style={{ width: "135px" }} />}
      <col style={{ width: "125px" }} />
      <col style={{ width: "200px" }} />
      <col style={{ width: "135px" }} />
      {!isOnlyTeamMember && <col style={{ width: "140px" }} />}
      <col style={{ width: "135px" }} />
      <col style={{ width: "100px" }} />
    </colgroup>
  );

  /* =====================================================
     RENDER
  ===================================================== */

  if (selectedDetailItem) {
    const isManual =
      selectedDetailItem.is_manual === true ||
      !selectedDetailItem.thread ||
      !selectedDetailItem.thread.emails ||
      selectedDetailItem.thread.emails.length === 0 ||
      !selectedDetailItem.thread.emails.some((e) => e && (e.email_storage_path || (e.msg_id && e.msg_id > 0 && e.msg_id !== selectedDetailItem.id)));

    if (isManual) {
      return (
        <ClassicAssignmentDetails
          item={selectedDetailItem}
          isOnlyTeamMember={isOnlyTeamMember}
          onBack={() => {
            goToRequestPoolList();
            onBack?.();
          }}
          onSaved={() => {
            loadAssignments();
            handleLoadItemDetails(selectedDetailItem.id, selectedDetailItem.organization);
          }}
        />
      );
    }

    const threadData = selectedDetailItem.thread || {
      conversation_id: `CONV_${selectedDetailItem.id}`,
      emails: [selectedReviewRow || selectedDetailItem],
      thread_count: 1,
      pending_count: 1
    };
    const activeRow = selectedReviewRow || selectedDetailItem.selectedRow || threadData.emails?.[0] || null;

    return (
      <div style={{ width: "100%", height: "100vh", background: "#f8fafc", overflow: "hidden" }}>
        <AssignmentThreadView
          workItem={selectedDetailItem}
          thread={threadData}
          activeTab={activeRow?.verified_flag ? "verified" : "pending"}
          selectedMsgId={selectedMsgId || activeRow?.msg_id}
          selectedRow={activeRow}
          reviewMode={true}
          isNarrow={false}
          previewLoading={previewLoading}
          previewUrl={inlinePreviewUrl}
          previewKind={inlinePreviewKind}
          previewError={inlinePreviewError}
          onSelectEmail={(email) => {
            setSelectedReviewRow(email);
            setSelectedMsgId(email?.msg_id);
            setInlinePreviewUrl(null);
            setInlinePreviewError(null);
            if (email?.email_storage_path) {
              handlePreview(email.email_storage_path);
            } else if (email?.attachments?.[0]?.path) {
              handlePreview(email.attachments[0].path);
            }
          }}
          onCollapse={() => {
            goToRequestPoolList();
            onBack?.();
          }}
          onCloseReview={() => {
            goToRequestPoolList();
            onBack?.();
          }}
          onTransferSuccess={() => {
            goToRequestPoolList();
            loadAssignments();
            onBack?.();
          }}
          onPreview={handlePreview}
          onSaved={(updatedData) => {
            if (updatedData) {
              const newAssignee = updatedData.assigned_to || updatedData.assignedTo;
              setSelectedDetailItem((prev) => {
                if (!prev) return prev;
                const prevEmails = prev.thread?.emails || [];
                const updatedEmails = prevEmails.map((e) =>
                  String(e.msg_id) === String(updatedData.msg_id)
                    ? { ...e, ...updatedData }
                    : e
                );
                return {
                  ...prev,
                  assignedTo: newAssignee || prev.assignedTo,
                  assigned_to: newAssignee || prev.assigned_to,
                  policy_number: updatedData.policy_number || prev.policy_number,
                  document_type: updatedData.document_type || prev.document_type,
                  thread: prev.thread
                    ? { ...prev.thread, emails: updatedEmails }
                    : prev.thread,
                };
              });
              setSelectedReviewRow((prev) =>
                prev && String(prev.msg_id) === String(updatedData.msg_id)
                  ? { ...prev, ...updatedData, assigned_to: newAssignee || prev.assigned_to, assignedTo: newAssignee || prev.assignedTo }
                  : prev
              );
            }
            if (updatedData?.is_closed) {
              goToRequestPoolList();
              loadAssignments();
            } else {
              loadAssignments();
              if (selectedDetailItem?.id) {
                handleLoadItemDetails(selectedDetailItem.id, selectedDetailItem.organization);
              }
            }
          }}
        />
      </div>
    );
  }

  if (isDirectLink && !selectedDetailItem) {
    if (error) {
      return (
        <div style={{ width: "100%", height: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px", padding: "20px", boxSizing: "border-box" }}>
          <div style={{ color: "#ef4444", fontSize: "14px", fontWeight: "600", textAlign: "center" }}>
            {error}
          </div>
          <button
            type="button"
            onClick={goToRequestPoolList}
            style={{
              padding: "8px 18px",
              borderRadius: "8px",
              background: "#2563eb",
              color: "#ffffff",
              border: "none",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Back to {fromParam === "manual-assignment" ? "Manual Assignment" : "Request Pool"}
          </button>
        </div>
      );
    }

    return (
      <div style={{ width: "100%", height: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px" }}>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{
          width: "36px",
          height: "36px",
          border: "3px solid #dbeafe",
          borderTop: "3px solid #2563eb",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite"
        }} />
        <p style={{ margin: 0, color: "#64748b", fontSize: "13px", fontWeight: "500" }}>
          Loading conversation thread...
        </p>
      </div>
    );
  }

  if (historyModalItem) {
    return (
      <div style={s.page} id="ado-view-assignments-container">
        <RequestHistoryModal
          isOpen={true}
          onClose={() => setHistoryModalItem(null)}
          workItem={historyModalItem}
          isFullPage={true}
        />
      </div>
    );
  }

  return (
    <div style={s.page} id="ado-view-assignments-container">
      <style>{`
        #ado-view-assignments-container select:focus,
        #ado-view-assignments-container .ado-date-input:focus,
        #ado-view-assignments-container .search-box:focus-within {
          border-color: ${THEME} !important;
          box-shadow: 0 0 0 3px ${THEME_LIGHT} !important;
          outline: none !important;
        }
        #ado-view-assignments-container .search-input:focus {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .sortable-th {
          transition: all 0.15s ease;
        }
        .sortable-th:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .history-icon-btn {
          width: auto;
          height: auto;
          min-height: 22px;
          padding: 3px 8px;
          border: 1px solid #dbeafe;
          background: #eff6ff;
          border-radius: 6px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          color: #2563eb;
          font-size: 11.5px;
          font-weight: 600;
          white-space: nowrap;
          margin-top: 3px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .history-icon-btn:hover,
        .history-icon-btn:focus-visible {
          background: #dbeafe;
          border-color: #93c5fd;
          color: #1d4ed8;
          outline: none;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
        }
        .devOpsLink {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px;
          background-color: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 7px;
          color: #1d4ed8;
          font-weight: 700;
          font-size: 12px;
          text-decoration: none;
          font-variant-numeric: tabular-nums;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 2px rgba(37, 99, 235, 0.06);
          cursor: pointer;
        }
        .devOpsLink:hover {
          background-color: #2563eb;
          color: #ffffff !important;
          border-color: #1d4ed8;
          box-shadow: 0 4px 10px rgba(37, 99, 235, 0.3);
          transform: translateY(-1px);
          text-decoration: none !important;
        }
        .devOpsLinkIcon {
          color: #3b82f6;
          transition: transform 0.2s ease, color 0.2s ease;
          flex-shrink: 0;
        }
        .devOpsLink:hover .devOpsLinkIcon {
          color: #ffffff !important;
          transform: translate(1px, -1px);
        }
        .assignment-tr {
          transition: all 0.1s ease;
        }
        .assignment-tr:hover {
          background-color: #f8fafc !important;
          box-shadow: inset 4px 0 0 ${THEME};
        }

        /* ───────── RESPONSIVE (Cards activate when sidebar hides at ≤ 980px) ───────── */
        @media (max-width: 980px) {
          #ado-view-assignments-container .rp-filter-actions {
            width: 100%;
            justify-content: flex-end;
          }
          #ado-view-assignments-container .rp-filter-left > * {
            flex: 1 1 100% !important;
            max-width: 100% !important;
          }
          #ado-view-assignments-container .rp-table {
            min-width: 0 !important;
          }
          #ado-view-assignments-container .rp-table colgroup,
          #ado-view-assignments-container .rp-table thead {
            display: none !important;
          }
          #ado-view-assignments-container .rp-table,
          #ado-view-assignments-container .rp-table tbody,
          #ado-view-assignments-container .rp-table tr {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box;
          }
          #ado-view-assignments-container .rp-table tr {
            padding: 10px 14px;
            border-bottom: 1px solid #e2e8f0;
          }
          #ado-view-assignments-container .rp-table td {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            padding: 6px 0 !important;
            text-align: left !important;
            overflow: visible !important;
            box-sizing: border-box;
          }
          #ado-view-assignments-container .rp-table td::before {
            content: attr(data-label);
            flex-shrink: 0;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #64748b;
          }
          #ado-view-assignments-container .rp-table td[data-label="Title"] {
            align-items: flex-start !important;
            max-width: 100% !important;
          }
          #ado-view-assignments-container .rp-table td[data-label="Title"]::before {
            margin-top: 3px;
          }
          #ado-view-assignments-container .rp-table td[data-label="Title"] > div {
            flex: 1;
            min-width: 0;
            max-width: calc(100% - 60px) !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-end !important;
            text-align: right !important;
          }
          #ado-view-assignments-container .rp-table td[data-label="Title"] span {
            max-width: 100% !important;
            text-align: right !important;
          }
          #ado-view-assignments-container .rp-table td[data-label="Title"] .inline-tags {
            justify-content: flex-end !important;
          }
          #ado-view-assignments-container .rp-table .inline-assignee-dropdown-wrap {
            width: auto !important;
            max-width: 100% !important;
            justify-content: flex-end !important;
          }
        }
      `}</style>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.breadcrumb}>
          <span>Document Index Hub</span>
          {" "}
          &gt;{" "}
          <span style={{ color: "#0f172a", fontWeight: "600" }}>Request Pool</span>
        </div>
        <h1 style={s.title}>Request Pool</h1>
        <p style={s.sub}>View and track work items.</p>
      </div>

      {/* RESPONSIVE FILTER BAR */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          padding: "10px 14px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          boxShadow: "0 1px 2px rgba(15,23,42,0.02)",
        }}
      >
        {/* Left Filters: Search ID, Sprint, Assignee */}
        <div
          className="rp-filter-left"
          style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 500px", flexWrap: "wrap", minWidth: 0 }}
        >
          {/* Search By Work Item ID */}
          <form
            onSubmit={handleSearchSubmit}
            style={{ display: "flex", alignItems: "center", flex: "1 1 220px", maxWidth: "340px", minWidth: 0, height: "36px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "0 4px 0 10px",
                height: "36px",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Search size={14} color="#64748b" style={{ marginRight: 6, flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                style={{
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  width: "100%",
                  minWidth: 0,
                  fontSize: "12.5px",
                  fontWeight: "600",
                  color: "#0f172a",
                }}
              />
              <button
                type="submit"
                style={{
                  background: THEME,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0 10px",
                  height: "28px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Search
              </button>
            </div>
          </form>

          {/* Sprint / Iteration */}
          <div style={{ flex: "1 1 160px", minWidth: "150px", maxWidth: "220px" }}>
            <SearchableSelect
              options={iterationOptions}
              value={selectedIteration}
              onChange={(val) => setSelectedIteration(val)}
              disabled={selectedProject === "all" || loading}
              placeholder="All Sprints"
            />
          </div>

          {/* Assignee */}
          {!isOnlyTeamMember && (
            <div style={{ flex: "1 1 160px", minWidth: "150px", maxWidth: "220px" }}>
              <SearchableSelect
                options={assigneeOptions}
                value={selectedAssignee}
                onChange={(val) => setSelectedAssignee(val)}
                disabled={selectedProject === "all" || loading}
                placeholder="All Assignees"
              />
            </div>
          )}
        </div>

        {/* Right Actions: Reset & Refresh */}
        <div className="rp-filter-actions" style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "0 12px",
              height: "36px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              fontSize: "12.5px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s ease",
              boxSizing: "border-box",
            }}
          >
            <RotateCcw size={13} color="#64748b" />
            Reset
          </button>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "0 14px",
              height: "36px",
              borderRadius: "8px",
              border: "none",
              background: THEME,
              color: "#ffffff",
              fontSize: "12.5px",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "all 0.15s ease",
              boxSizing: "border-box",
            }}
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            {loading ? "Syncing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ERROR MESSAGE */}
      {error && <div style={s.errorBox}>{error}</div>}

      {/* LOADING INDICATOR / TABLE */}
      {assignments.length === 0 && loading ? (
        <div style={s.tableWrapper}>
          <div style={s.tableScroll}>
            <table style={s.table} className="rp-table">
              {renderColGroup()}
              <thead>
                <tr style={s.tableHeaderRow}>
                  {!isOnlyTeamMember && <th style={s.th}>Work Item ID</th>}
                  <th style={s.th}>User Story ID</th>
                  <th style={s.th}>Work Item Title</th>
                  <th style={s.th}>Created Date</th>
                  {!isOnlyTeamMember && <th style={s.th}>Assignee</th>}
                  <th style={s.th}>ETA Date</th>
                  <th style={s.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`va-skel-${i}`} style={s.tr}>
                    {!isOnlyTeamMember && <td style={s.tdId} data-label="Work Item ID"><div style={{ ...s.skeletonBar, width: "90px" }} /></td>}
                    <td style={s.td} data-label="User Story ID"><div style={{ ...s.skeletonBar, width: "60px" }} /></td>
                    <td style={s.tdTitle} data-label="Title"><div style={{ ...s.skeletonBar, width: "80%" }} /></td>
                    <td style={s.td} data-label="Created"><div style={{ ...s.skeletonBar, width: "90px" }} /></td>
                    {!isOnlyTeamMember && <td style={s.td} data-label="Assignee"><div style={{ ...s.skeletonBar, width: "130px" }} /></td>}
                    <td style={s.td} data-label="ETA"><div style={{ ...s.skeletonBar, width: "90px" }} /></td>
                    <td style={s.td} data-label="Status"><div style={{ ...s.skeletonBar, width: "70px" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : assignments.length === 0 && !loading ? (
        /* EMPTY STATE */
        <div style={s.emptyState}>
          <Eye size={48} color="#cbd5e1" style={{ marginBottom: 16 }} />
          <h3 style={s.emptyTitle}>No assignments match your query</h3>
          <p style={s.emptySub}>Try adjusting your sprint or ID search filter.</p>
        </div>
      ) : (
        /* TABLE LISTING */
        <div style={{ ...s.tableWrapper, position: "relative" }}>
          {loading && (
            <div style={s.overlayLoader}>
              <RefreshCw size={20} color={THEME} style={{ animation: "spin 0.8s linear infinite" }} />
              <span>Fetching assignments from Azure DevOps…</span>
            </div>
          )}
          <div style={s.tableScroll}>
            <table style={s.table} className="rp-table">
              {renderColGroup()}
              <thead>
                <tr style={s.tableHeaderRow}>
                  {!isOnlyTeamMember && (
                    <th
                      style={{
                        ...s.th,
                        cursor: "pointer",
                        userSelect: "none",
                        color: sortBy === "id" ? THEME : "#475569",
                        fontWeight: sortBy === "id" ? "700" : "600"
                      }}
                      onClick={() => handleSort("id")}
                      title={
                        sortBy === "id"
                          ? sortOrder === "desc"
                            ? "Sorted by Work Item ID (High to Low). Click to sort Low to High."
                            : "Sorted by Work Item ID (Low to High). Click to sort High to Low."
                          : "Click to sort by Work Item ID"
                      }
                      className="sortable-th"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <span>Work Item ID</span>
                        {sortBy === "id" ? (
                          sortOrder === "asc" ? <ArrowUp size={13} color={THEME} /> : <ArrowDown size={13} color={THEME} />
                        ) : (
                          <ArrowUpDown size={13} color="#94a3b8" />
                        )}
                      </div>
                    </th>
                  )}
                  <th style={{ ...s.th, userSelect: "none", color: "#475569", fontWeight: "600" }}>
                    <span>User Story ID</span>
                  </th>
                  <th style={s.th}>Work Item Title</th>
                  <th
                    style={{
                      ...s.th,
                      cursor: "pointer",
                      userSelect: "none",
                      color: sortBy === "createdDate" ? THEME : "#475569",
                      fontWeight: sortBy === "createdDate" ? "700" : "600"
                    }}
                    onClick={() => handleSort("createdDate")}
                    title={
                      sortBy === "createdDate"
                        ? sortOrder === "desc"
                          ? "Sorted by Created Date (Newest First). Click to sort Oldest First."
                          : "Sorted by Created Date (Oldest First). Click to sort Newest First."
                        : "Click to sort by Created Date"
                    }
                    className="sortable-th"
                  >
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <span>Created Date</span>
                      {sortBy === "createdDate" ? (
                        sortOrder === "asc" ? <ArrowUp size={13} color={THEME} /> : <ArrowDown size={13} color={THEME} />
                      ) : (
                        <ArrowUpDown size={13} color="#94a3b8" />
                      )}
                    </div>
                  </th>
                  {!isOnlyTeamMember && <th style={s.th}>Assignee</th>}
                  <th
                    style={{
                      ...s.th,
                      cursor: "pointer",
                      userSelect: "none",
                      color: sortBy === "targetDate" ? THEME : "#475569",
                      fontWeight: sortBy === "targetDate" ? "700" : "600"
                    }}
                    onClick={() => handleSort("targetDate")}
                    title={
                      sortBy === "targetDate"
                        ? sortOrder === "desc"
                          ? "Sorted by ETA Date (Newest First). Click to sort Oldest First."
                          : "Sorted by ETA Date (Oldest First). Click to sort Newest First."
                        : "Click to sort by ETA Date"
                    }
                    className="sortable-th"
                  >
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <span>ETA Date</span>
                      {sortBy === "targetDate" ? (
                        sortOrder === "asc" ? <ArrowUp size={13} color={THEME} /> : <ArrowDown size={13} color={THEME} />
                      ) : (
                        <ArrowUpDown size={13} color="#94a3b8" />
                      )}
                    </div>
                  </th>
                  <th
                    style={{
                      ...s.th,
                      cursor: "pointer",
                      userSelect: "none",
                      color: sortBy === "status" ? THEME : "#475569",
                      fontWeight: sortBy === "status" ? "700" : "600"
                    }}
                    onClick={() => handleSort("status")}
                    title={
                      sortBy === "status"
                        ? sortOrder === "desc"
                          ? "Sorted by Status (Z to A). Click to sort A to Z."
                          : "Sorted by Status (A to Z). Click to sort Z to A."
                        : "Click to sort by Status"
                    }
                    className="sortable-th"
                  >
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      <span>Status</span>
                      {sortBy === "status" ? (
                        sortOrder === "asc" ? <ArrowUp size={13} color={THEME} /> : <ArrowDown size={13} color={THEME} />
                      ) : (
                        <ArrowUpDown size={13} color="#94a3b8" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {assignments
                  .filter((item) => {
                    if (!item) return false;
                    if (!item.workItemNum) return false;
                    if (!item.state) return true;
                    const st = item.state.toLowerCase();
                    return !["closed", "done", "completed", "resolved", "removed", "inactive", "cancelled"].includes(st);
                  })
                  .map((item) => {
                    const linkUrl = `https://dev.azure.com/${encodeURIComponent(item.organization)}/${encodeURIComponent(item.project)}/_workitems/edit/${item.id}`;

                    // State Pill styling
                    const stateLower = (item.state || "").toLowerCase();
                    let stateBg = "#e2e8f0";
                    let stateColor = "#475569";
                    if (stateLower === "active" || stateLower === "in progress" || stateLower === "committed") {
                      stateBg = "#eff6ff";
                      stateColor = "#1d4ed8";
                    } else if (stateLower === "done" || stateLower === "completed" || stateLower === "closed") {
                      stateBg = "#f0fdf4";
                      stateColor = "#15803d";
                    }

                    const etaString = item.targetDate ? new Date(item.targetDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      timeZone: "UTC"
                    }) : "Not Set";

                    const createdDateString = item.createdDate ? new Date(item.createdDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      timeZone: "UTC"
                    }) : "Not Set";

                    return (
                      <tr
                        key={item.id}
                        className="assignment-tr"
                        style={s.tr}
                        onClick={() => handleLoadItemDetails(item.id, item.organization)}
                      >
                        {/* 1. Work Item Num as Work Item ID with History Button underneath */}
                        {!isOnlyTeamMember && (
                          <td style={s.tdId} data-label="Work Item ID" onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                              <span
                                style={{
                                  fontWeight: "700",
                                  color: item.workItemNum ? "#1e293b" : "#94a3b8",
                                  fontSize: "12px",
                                  userSelect: "text",
                                  cursor: "text",
                                }}
                                title={`Work Item ID: ${item.workItemNum || "—"}`}
                              >
                                {item.workItemNum || "—"}
                              </span>
                              <button
                                type="button"
                                className="history-icon-btn"
                                onClick={() => setHistoryModalItem(item)}
                                title="View history"
                                aria-label="View history"
                              >
                                <History size={12} color="#2563eb" />
                                <span>History</span>
                              </button>
                            </div>
                          </td>
                        )}

                        {/* 2. User Story ID (Azure Link) */}
                        <td style={s.td} data-label="User Story ID" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="devOpsLink"
                            title={`Open User Story ${item.id} directly in Azure DevOps`}
                            style={{ display: "inline-flex", alignItems: "center", gap: "4px", textDecoration: "none", color: "#2563eb", fontWeight: "600", fontSize: "12px", userSelect: "text" }}
                          >
                            <span>{item.id}</span>
                            <ExternalLink size={12} className="devOpsLinkIcon" />
                          </a>
                          {isOnlyTeamMember && (
                            <button
                              type="button"
                              className="history-icon-btn"
                              onClick={() => setHistoryModalItem(item)}
                              title="View history"
                              aria-label="View history"
                            >
                              <History size={12} color="#2563eb" />
                              <span>History</span>
                            </button>
                          )}
                        </td>

                        {/* 3. Work Item Title (CSS ellipsis) */}
                        <td style={s.tdTitle} data-label="Title" title={item.title}>
                          <div style={s.titleContainer}>
                            <span style={s.titleText} title={item.title}>
                              {item.title}
                            </span>
                            {item.tags && (
                              <div style={s.inlineTags}>
                                {item.tags.split(";").map((tag) => tag.trim()).filter(Boolean).map((tag, idx) => (
                                  <span key={idx} style={s.tagBadgeSmall}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* 4. Azure Created Date */}
                        <td style={s.td} data-label="Created">
                          <div style={s.etaCell}>
                            <Calendar size={13} color="#64748b" style={{ marginRight: 6, flexShrink: 0 }} />
                            <span style={{ color: item.createdDate ? "#475569" : "#94a3b8", fontSize: "12px" }}>
                              {createdDateString}
                            </span>
                          </div>
                        </td>

                        {/* 5. Assignee with Direct Text Search & Dropdown */}
                        {!isOnlyTeamMember && (
                          <td style={{ ...s.td, position: "relative" }} data-label="Assignee" onClick={(e) => e.stopPropagation()}>
                            <InlineAssigneeCell
                              item={item}
                              assignableUsers={assignableUsers}
                              inlineUpdatingId={inlineUpdatingId}
                              onAssignChange={handleInlineAssigneeChange}
                            />
                          </td>
                        )}

                        {/* 6. ETA Date */}
                        <td style={s.td} data-label="ETA">
                          <div style={s.etaCell}>
                            <Calendar size={13} color="#64748b" style={{ marginRight: 6, flexShrink: 0 }} />
                            <span style={{ color: item.targetDate ? "#475569" : "#94a3b8", fontSize: "12px" }}>{etaString}</span>
                          </div>
                        </td>

                        {/* 7. Status */}
                        <td style={s.td} data-label="Status">
                          <span style={{ ...s.stateBadge, color: stateColor, background: stateBg }}>
                            {item.state}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PAGINATION BAR WITH SMART WINDOWING & GO TO PAGE */}
      {totalPages > 1 && (
        <div style={s.paginationBar}>
          <button
            style={currentPage === 1 ? s.pageBtnDisabled : s.pageBtn}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
            {(() => {
              const pages = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (currentPage > 3) pages.push("...");
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                for (let i = start; i <= end; i++) {
                  if (!pages.includes(i)) pages.push(i);
                }
                if (currentPage < totalPages - 2) pages.push("...");
                if (!pages.includes(totalPages)) pages.push(totalPages);
              }

              return pages.map((item, idx) => {
                if (item === "...") {
                  return (
                    <span key={`va-ellipsis-${idx}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: "13px", fontWeight: "700" }}>
                      …
                    </span>
                  );
                }
                const isCurrent = item === currentPage;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    style={{
                      minWidth: "32px",
                      height: "32px",
                      padding: "0 6px",
                      fontSize: "12px",
                      fontWeight: isCurrent ? "700" : "500",
                      borderRadius: "6px",
                      border: isCurrent ? `1px solid ${THEME}` : "1px solid #e2e8f0",
                      background: isCurrent ? THEME : "#ffffff",
                      color: isCurrent ? "#ffffff" : "#475569",
                      cursor: "pointer",
                      outline: "none"
                    }}
                  >
                    {item}
                  </button>
                );
              });
            })()}
          </div>

          <button
            style={currentPage === totalPages ? s.pageBtnDisabled : s.pageBtn}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>

          {/* GO TO PAGE FEATURE */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "8px", borderLeft: "1px solid #e2e8f0", paddingLeft: "10px" }}>
            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>Go to:</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={gotoPageInput}
              onChange={(e) => setGotoPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const targetP = parseInt(gotoPageInput, 10) || currentPage;
                  if (targetP >= 1 && targetP <= totalPages) {
                    setCurrentPage(targetP);
                    setGotoPageInput("");
                  }
                }
              }}
              placeholder={String(currentPage)}
              style={{
                width: "52px",
                height: "30px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "600",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                outline: "none"
              }}
            />
            <button
              type="button"
              onClick={() => {
                const targetP = parseInt(gotoPageInput, 10) || currentPage;
                if (targetP >= 1 && targetP <= totalPages) {
                  setCurrentPage(targetP);
                  setGotoPageInput("");
                }
              }}
              style={{
                height: "30px",
                padding: "0 10px",
                fontSize: "12px",
                fontWeight: "600",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#334155",
                cursor: "pointer",
                outline: "none"
              }}
            >
              Go
            </button>
          </div>
        </div>
      )}

      {/* REQUEST HISTORY MODAL */}
      <RequestHistoryModal
        isOpen={Boolean(historyModalItem)}
        onClose={() => setHistoryModalItem(null)}
        workItem={historyModalItem}
      />
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    boxSizing: "border-box",
    maxWidth: "100%",
    padding: "clamp(12px, 4vw, 24px)",
  },
  header: { marginBottom: "16px" },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "24px",
    padding: "16px 0",
    borderTop: "1px solid #e2e8f0",
  },
  pageBtn: {
    padding: "8px 16px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#475569",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.15s ease",
  },
  pageBtnDisabled: {
    padding: "8px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    background: "#f1f5f9",
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "not-allowed",
    outline: "none",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "8px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "50px 20px",
    textAlign: "center",
  },
  emptyTitle: {
    margin: "0 0 4px 0",
    fontSize: "16px",
    color: "#0f172a",
    fontWeight: "600",
  },
  emptySub: {
    margin: 0,
    fontSize: "13px",
    color: "#64748b",
  },
  tableWrapper: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
    overflow: "hidden",
    boxSizing: "border-box",
    width: "100%",
  },
  tableScroll: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    width: "100%",
  },
  table: {
    width: "100%",
    minWidth: "960px",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: "12px",
  },
  tableHeaderRow: {
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  th: {
    padding: "12px 12px",
    fontWeight: "600",
    color: "#475569",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
  },
  td: {
    padding: "12px 12px",
    color: "#334155",
    verticalAlign: "middle",
    overflow: "hidden",
    minWidth: 0,
  },
  tdId: {
    padding: "12px 12px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tdTitle: {
    padding: "12px 12px",
    verticalAlign: "middle",
    overflow: "hidden",
    minWidth: 0,
    maxWidth: "200px",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
    maxWidth: "100%",
  },
  titleText: {
    fontWeight: "500",
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "200px",
    display: "block",
  },
  inlineTags: {
    display: "flex",
    gap: "4px",
    flexWrap: "wrap",
    minWidth: 0,
  },
  tagBadgeSmall: {
    background: "#f1f5f9",
    color: "#475569",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "9px",
    fontWeight: "500",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  etaCell: {
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  stateBadge: {
    fontSize: "11px",
    fontWeight: "600",
    padding: "3px 8px",
    borderRadius: "12px",
    display: "inline-block",
    whiteSpace: "nowrap",
  },
};
